import { useState, useEffect } from 'react';
import './styles.css';
import TableControls from './controls';

export default function Table({
  headers,
  rows,
  csvHeaders,
  csvRows,
  // Optional richer download config. When present, replaces the
  // single-download `csvHeaders` / `csvRows` path so the toolbar can
  // render multiple options (e.g. Overall vs. Level scores CSVs).
  // Each entry is either "static" (`{label, headers, rows, filename}`
  // — Table generates the CSV inline) or "delegated"
  // (`{label, filename, onClick, isLoading}` — parent handles the
  // click and fires its own download, useful for lazy-fetched data).
  downloads,
  rowsPerPageOptions = [15, 25, 50]
}) {
  const [sortConfig, setSortConfig] = useState({ key: 0, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRows, setFilteredRows] = useState(rows);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredRows(rows);
    } else {
      const term = searchTerm.toLowerCase().trim();
      const filtered = rows.filter(row => {
        const firstCell = Array.isArray(row) ? row[0] : row.cells[0].value;
        return firstCell.toString().toLowerCase().includes(term);
      });
      setFilteredRows(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, rows]);

  const sortedRows = [...filteredRows].sort((a, b) => {
    if (sortConfig.key === null) return 0;
    
    const aValue = Array.isArray(a) 
      ? a[sortConfig.key]?.toLowerCase() || ''
      : a.cells[sortConfig.key]?.value?.toLowerCase() || '';
    const bValue = Array.isArray(b)
      ? b[sortConfig.key]?.toLowerCase() || ''
      : b.cells[sortConfig.key]?.value?.toLowerCase() || '';
    
    return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
  });

  const totalRows = filteredRows.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const paginatedRows = sortedRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handleSort = (index) => {
    setSortConfig(prev => ({
      key: index,
      direction: prev.key === index && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (direction) => {
    setCurrentPage(prev => Math.max(1, Math.min(totalPages, prev + direction)));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const cleanHeaderForCSV = (header) => {
    return header.replace(/<br>/g, ' - ')
                 .replace(/<[^>]*>/g, '')
                 .replace(/"/g, '""');
  };

  const buildCSV = (dlHeaders, dlRows) => {
    if (!dlHeaders?.length || !dlRows?.length) return '';
    const cleanHeaders = dlHeaders.map(header => cleanHeaderForCSV(header));
    const csvFileHeaders = cleanHeaders.map(header => `"${header}"`).join(',');
    const csvFileRows = dlRows.map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    return `${csvFileHeaders}\n${csvFileRows}`;
  };

  const triggerCsvDownload = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const defaultFilename = () => {
    const date = new Date();
    return `instructor_scores_${date.toISOString().split('T')[0]}.csv`;
  };

  // Normalize the download config: prefer the newer `downloads` array;
  // fall back to legacy `csvHeaders` / `csvRows` (rendered as a single
  // "Download scores" button, preserving pre-refactor behavior).
  const resolvedDownloads = downloads?.length
    ? downloads
    : (csvHeaders && csvRows
        ? [{ label: 'Download scores', headers: csvHeaders, rows: csvRows, filename: defaultFilename() }]
        : []);

  const handleStaticDownload = (dl) => {
    const csv = buildCSV(dl.headers, dl.rows);
    if (!csv) return;
    triggerCsvDownload(csv, dl.filename || defaultFilename());
  };

  return (
    <div className="data-table-container">
      <div className="data-table-heading">
        <div className="data-table-search-input-container">
          <input
            type="text"
            placeholder="Search by name"
            value={searchTerm}
            onChange={handleSearch}
            className="data-table-search-input"
          />
        </div>
        {resolvedDownloads.length > 0 && (
          <div className="data-table-downloads">
            {resolvedDownloads.map((dl, i) => {
              const isDelegated = typeof dl.onClick === 'function';
              const onClick = isDelegated ? dl.onClick : () => handleStaticDownload(dl);
              return (
                <button
                  key={i}
                  onClick={onClick}
                  disabled={dl.isLoading}
                  className={`button-download-scores${dl.isLoading ? ' loading' : ''}`}
                >
                  {dl.isLoading ? 'Preparing…' : dl.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="data-table-scrollable">
        <table className="data-table">
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th 
                  key={index} 
                  onClick={() => handleSort(index)}
                  className={sortConfig.key === index ? `active ${sortConfig.direction}` : ''}
                  dangerouslySetInnerHTML={{ __html: header }}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {Array.isArray(row) ? (
                  row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{cell}</td>
                  ))
                ) : (
                  row.cells.map((cell, cellIndex) => (
                    <td 
                      key={cellIndex}
                      style={cell.style || {}}
                    >
                      {cell.value}
                    </td>
                  ))
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TableControls
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        onRowsPerPageChange={setRowsPerPage}
        currentPage={currentPage}
        onCurrentPageChange={setCurrentPage}
        totalRows={totalRows}
        onPageChange={handlePageChange}
        totalPages={totalPages}
      />
    </div>
  );
}
